import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Spinner, Textarea } from '@nextui-org/react';
import { apiFetch } from '../lib/api';
import { ALL_LANGS, APP_FONT_URL, EDIT_LANG_LIST as LANGS, HomePlanModal, HomeShowcase, OTHER_LANGS } from '../lib/homeShowcase';
import TranslateDialog from './TranslateDialog';

// Inicio de la app → el popup «Incluido en Premium»: el que sale al pulsar en
// los carruseles algo que el plan del usuario no incluye. Aquí se cambian sus
// textos: la etiqueta, el título, la descripción, las ventajas y los dos
// botones. A la izquierda el formulario; a la derecha, el popup tal y como lo
// pinta la app (mismas clases y misma hoja de estilos: lib/homeShowcase.js).
//
// Un campo vacío = el texto de fábrica, que la app ya dice en sus 14 idiomas.
// Lo que se escribe va en español e inglés y, con «Traducir con ChatGPT», en
// los otros doce (igual que en los carruseles).
//
// El precio y el código de descuento NO se escriben aquí: los pone la app (el
// plan y el cupón vigente, los mismos de Gestionar suscripción), para que el
// popup nunca diga una cifra distinta de la que cobra Stripe.
// Worker: cf-worker/src/api/home.js (D1 app_config['home_plan_modal']); modelo
// compartido con la app: renderer/tabs/inicio/componentes/plan-modelo.js.

const ENDPOINT = '/api/backoffice/home/plan-modal';
const P = HomePlanModal;
const L = HomeShowcase;   // idiomas: qué le falta a un texto, en qué idioma se ve

const LABELS = {
  kicker: ['Etiqueta', 'La línea pequeña de arriba.'],
  title: ['Título', 'Escribe {name} donde va el nombre de lo que ha pulsado.'],
  desc: ['Descripción', 'Una o dos frases sobre el plan.'],
  pay: ['Botón principal', 'Abre el pago de Premium mensual.'],
  more: ['Botón secundario', 'Abre todos los planes.']
};
const SAMPLE_NAME = { es: 'Ruleta de premios', en: 'Prize wheel' };
// La línea que la app pone sola según el plan de quien mira (aquí, la de un usuario gratuito).
const NOW_LINE = { es: 'Tu plan gratuito no lo incluye.', en: "Your free plan doesn't include it." };
const MINI_FIELD = 'h-8 w-full min-w-0 rounded-md border border-default-200 bg-default-100 px-2 text-xs text-foreground outline-none transition-colors focus:border-primary';

const text = (value) => String(value == null ? '' : value).trim();
const blank = () => ({ es: '', en: '' });

// ── Traducciones (el mismo asistente de los carruseles) ──────────
// Cada texto con español, con su clave para el prompt: los cinco campos por
// su nombre y las ventajas por su puesto («benefit1»).
function allTexts(form) {
  const out = P.FIELD_IDS.map((id) => ({ id, where: id, value: form[id], limit: P.FIELDS[id].limit }));
  form.benefits.forEach((value, index) => out.push({ id: `benefit${index + 1}`, where: index, value, limit: P.BENEFIT_LIMIT }));
  return out.filter((f) => text(f.value.es)).map((f) => ({ ...f, es: text(f.value.es) }));
}

function translationFields(form, { all, stale }) {
  return allTexts(form).filter((f) => all || stale.has(f.id) || L.missingLangs(f.value).length);
}

function translationPrompt(fields) {
  const targets = ALL_LANGS.filter((l) => l.id !== 'es').map((l) => ({ code: l.id, name: l.name }));
  return [
    'Traduce estos textos de un aviso de TikControl (una app para creadores de TikTok LIVE) desde español al resto de idiomas.',
    'Es el aviso que sale cuando alguien pulsa algo que su plan no incluye: explica el plan Premium y ofrece contratarlo.',
    '',
    'Devuelve SOLO JSON valido, sin markdown ni explicaciones, con esta forma exacta:',
    '{"translations":{"fieldKey":{"en":"...","fr":"..."}}}',
    '',
    'Reglas:',
    '- Mantén exactamente las mismas claves de campo.',
    '- Usa exactamente estos codigos de idioma: ' + targets.map((l) => l.code).join(', '),
    '- Conserva {name} tal cual, sin traducirlo: ahí va el nombre de lo que se ha pulsado.',
    '- Largo máximo de cada texto, en caracteres: ' + fields.map((f) => `${f.id} ${f.limit}`).join(', ') + '.',
    '- pay y more son botones: muy cortos y naturales.',
    '- No traduzcas marcas ni nombres propios (TikControl, TikTok, LIVE, Premium, Performance).',
    '- Tono natural y cercano, como el de una app; nada de traducciones literales.',
    '- Texto plano: sin HTML, sin markdown y sin claves extra.',
    '- No uses comillas dobles rectas (") dentro de los textos: usa comillas tipograficas (« », “ ”) o escapalas como \\".',
    '',
    JSON.stringify({ sourceLanguage: 'es', targetLanguages: targets, fields: Object.fromEntries(fields.map((f) => [f.id, f.es])) }, null, 2)
  ].join('\n');
}

// El JSON pegado → el formulario con las traducciones puestas. { form, applied, done }
function applyTranslations(form, fields, parsed) {
  const root = parsed && typeof parsed === 'object' ? parsed : {};
  const t = root.translations && typeof root.translations === 'object' ? root.translations : root;
  const next = { ...form, benefits: form.benefits.slice() };
  const done = [];
  fields.forEach((f) => {
    const got = t[f.id];
    if (!got || typeof got !== 'object') return;
    const current = typeof f.where === 'number' ? next.benefits[f.where] : next[f.where];
    // El español ha cambiado desde que se preparó el prompt: esa traducción ya no es la suya.
    if (!current || text(current.es) !== f.es) return;
    const value = { ...current };
    let any = false;
    ALL_LANGS.forEach(({ id }) => {
      if (id === 'es' || typeof got[id] !== 'string' || !got[id].trim()) return;
      if (id === 'en' && text(value.en)) return;   // el inglés escrito a mano manda
      value[id] = got[id];
      any = true;
    });
    if (!any) return;
    if (typeof f.where === 'number') next.benefits[f.where] = value; else next[f.where] = value;
    done.push(f.id);
  });
  return { form: P.sanitize(next), applied: done.length, done };
}

function Icon({ children, size = 18 }) {
  return (
    <svg className="hm-ico" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
const LOCK = <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>;
const CHECK = <path d="m5 12.5 4.5 4.5L19 7.5" />;
const ARROW = <><path d="M5 12h14" /><path d="m13 6 6 6-6 6" /></>;
const CLOSE = <><path d="M6 6l12 12" /><path d="M18 6 6 18" /></>;

// ── Vista previa: el popup, como en la app ───────────────────────
function Preview({ form, lang }) {
  const copy = P.resolve(form, lang);
  const es = lang === 'es';
  const name = es ? SAMPLE_NAME.es : SAMPLE_NAME.en;
  return (
    <div className="hm-plan" role="img" aria-label="Vista previa del popup" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", animation: 'none', margin: '0 auto' }}>
      <span className="hm-plan-x" aria-hidden="true"><Icon>{CLOSE}</Icon></span>
      <span className="hm-plan-icon" aria-hidden="true"><Icon size={26}>{LOCK}</Icon></span>
      <span className="hm-plan-kicker">{copy.kicker}</span>
      <h3 className="hm-plan-title">{copy.title.split('{name}').join(name)}</h3>
      <p className="hm-plan-now">{es ? NOW_LINE.es : NOW_LINE.en}</p>
      <p className="hm-plan-desc">{copy.desc}</p>
      <ul className="hm-plan-list">
        {copy.benefits.map((benefit, i) => <li key={i}><Icon size={15}>{CHECK}</Icon><span>{benefit}</span></li>)}
      </ul>
      <div className="hm-plan-priceslot">
        <div className="hm-plan-price"><strong>$24.99{es ? '/mes' : '/mo'}</strong><span>{es ? 'Premium mensual' : 'Premium monthly'}</span></div>
      </div>
      <div className="hm-plan-actions">
        <button type="button" className="hm-btn hm-btn--gold hm-plan-pay" tabIndex={-1}><span>{copy.pay}</span><Icon size={16}>{ARROW}</Icon></button>
        <button type="button" className="hm-btn hm-btn--outline hm-plan-more" tabIndex={-1}><span>{copy.more}</span></button>
      </div>
    </div>
  );
}

// Los otros idiomas de un texto: cuántos tiene y, desplegado, cada uno, para
// verlo y corregirlo.
function OtherLangs({ value, limit, stale, canWrite, onChange }) {
  const [show, setShow] = useState(false);
  if (!text(value.es)) return null;
  const done = OTHER_LANGS.filter((l) => text(value[l.id]));
  const whole = done.length === OTHER_LANGS.length;
  return (
    <div className="mt-1" data-other-langs>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={`text-xs ${stale || !whole ? 'text-warning' : 'text-success'}`}>
          {stale
            ? 'Has cambiado el español: vuelve a traducir'
            : whole ? `Traducido a los ${OTHER_LANGS.length} idiomas restantes` : done.length ? `${done.length} de ${OTHER_LANGS.length} idiomas traducidos` : 'Sin traducir: en los demás idiomas se ve en inglés'}
        </span>
        <button type="button" className="ml-auto text-xs font-semibold text-primary" aria-expanded={show} onClick={() => setShow(!show)}>
          {show ? 'Ocultar' : 'Ver y corregir'}
        </button>
      </div>
      {show ? (
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {OTHER_LANGS.map((l) => (
            <label key={l.id} className="grid grid-cols-[70px_minmax(0,1fr)] items-center gap-2">
              <span className="truncate text-xs text-default-500" title={l.name}>{l.label}</span>
              <input
                dir="auto" className={MINI_FIELD} aria-label={l.name} readOnly={!canWrite} maxLength={Math.round(limit * L.TRANSLATION_SLACK)}
                placeholder="Se ve en inglés" value={value[l.id] || ''} onChange={(e) => onChange({ ...value, [l.id]: e.target.value })}
              />
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// Un texto: español e inglés a mano y, debajo, sus otros idiomas.
function TextRow({ label, hint, value, limit, factory, long, stale, canWrite, onChange }) {
  const Field = long ? Textarea : Input;
  return (
    <div className="rounded-lg border border-default-200 p-3">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <span className="text-sm font-semibold">{label}</span>
        {hint ? <span className="text-xs text-default-500">{hint}</span> : null}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {LANGS.map((lang) => (
          <Field
            key={lang.id} size="sm" aria-label={`${label} · ${lang.label}`} label={lang.label} labelPlacement="outside" maxLength={limit} isReadOnly={!canWrite}
            {...(long ? { minRows: 2, maxRows: 4 } : {})}
            placeholder={factory ? factory[lang.id] : ''} value={value[lang.id] || ''} onValueChange={(v) => onChange({ ...value, [lang.id]: v })}
          />
        ))}
      </div>
      <OtherLangs value={value} limit={limit} stale={stale} canWrite={canWrite} onChange={onChange} />
    </div>
  );
}

export default function HomePlanModalEditor({ canWrite }) {
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState({ json: '', updatedAt: null });
  const [lang, setLang] = useState('es');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);            // { type: 'ok' | 'warn' | 'error', text }
  const [translating, setTranslating] = useState(false);
  const [stale, setStale] = useState(() => new Set());     // textos cambiados en español después de traducirlos

  useEffect(() => {
    // La fuente de la app, para que la vista previa mida lo mismo.
    if (!document.querySelector(`link[href="${APP_FONT_URL}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = APP_FONT_URL;
      document.head.appendChild(link);
    }
  }, []);

  const apply = useCallback((data) => {
    const clean = P.sanitize(data.config);
    setForm(clean);
    setSaved({ json: JSON.stringify(clean), updatedAt: data.updatedAt || null });
  }, []);

  useEffect(() => {
    let alive = true;
    apiFetch(ENDPOINT, { cache: 'no-store' })
      .then((data) => { if (alive) apply(data); })
      .catch((e) => { if (alive) { setForm(P.sanitize(null)); setMessage({ type: 'error', text: e.message || 'No se pudo leer' }); } });
    return () => { alive = false; };
  }, [apply]);

  if (!form) return <div className="flex justify-center p-8"><Spinner /></div>;

  const dirty = JSON.stringify(P.sanitize(form)) !== saved.json;
  const toTranslate = translationFields(form, { all: false, stale });
  const ownTexts = translationFields(form, { all: true, stale });
  const hasOthers = (value) => OTHER_LANGS.some((l) => text(value[l.id]));

  // Cambiar el español de un texto ya traducido deja sus traducciones atrás: se apunta para volver a traducirlo.
  const touch = (id, before, value) => {
    if (text(before.es) !== text(value.es) && hasOthers(value)) setStale((set) => new Set(set).add(id));
  };
  const setField = (id, value) => { touch(id, form[id], value); setForm((f) => ({ ...f, [id]: value })); };
  const setBenefit = (index, value) => {
    touch(`benefit${index + 1}`, form.benefits[index], value);
    setForm((f) => ({ ...f, benefits: f.benefits.map((b, i) => (i === index ? value : b)) }));
  };
  // Al mover o quitar ventajas cambian sus puestos (y con ellos sus claves): las marcas de «cambiado» de las ventajas se sueltan.
  const reorder = (benefits) => {
    setStale((set) => new Set([...set].filter((id) => !id.startsWith('benefit'))));
    setForm((f) => ({ ...f, benefits }));
  };
  const moveBenefit = (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= form.benefits.length) return;
    const next = form.benefits.slice();
    [next[index], next[j]] = [next[j], next[index]];
    reorder(next);
  };

  const applyTranslated = (fields, parsed) => {
    const result = applyTranslations(form, fields, parsed);
    if (!result.applied) return result;
    setForm(result.form);
    setStale((set) => { const next = new Set(set); result.done.forEach((id) => next.delete(id)); return next; });
    setTranslating(false);
    const missing = fields.length - result.applied;
    setMessage({
      type: missing ? 'warn' : 'ok',
      text: `Traducciones puestas en ${result.applied} ${result.applied === 1 ? 'texto' : 'textos'}${missing ? ` (${missing} sin respuesta)` : ''}. Compruébalas con el idioma de la vista previa y guarda.`
    });
    return result;
  };

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      // Las ventajas a medio escribir (sin texto) no se guardan.
      const data = await apiFetch(ENDPOINT, { method: 'PUT', body: { config: P.sanitize(form) } });
      apply(data);
      setMessage({ type: 'ok', text: 'Guardado. Las apps abiertas lo enseñan en un minuto, más o menos, sin reiniciar.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'No se pudo guardar' });
    } finally {
      setBusy(false);
    }
  }

  const when = saved.updatedAt ? new Date(saved.updatedAt).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
  const tone = { ok: 'text-success', warn: 'text-warning', error: 'text-danger' };
  const empty = P.isEmpty(form);

  return (
    <section className="space-y-4 rounded-xl border border-default-200 bg-content1 p-5" data-plan-modal-editor>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold">Popup de elemento bloqueado</h2>
          <p className="text-sm text-default-500">
            El aviso «Incluido en Premium» que sale al pulsar en los carruseles algo que el plan del usuario no incluye. Un campo vacío usa
            el texto de fábrica (en gris), que la app ya tiene en sus {ALL_LANGS.length} idiomas. {when ? `Último guardado: ${when}.` : ''}
          </p>
        </div>
        <label className="flex shrink-0 items-center gap-2 text-xs font-semibold text-default-500">
          Vista previa en
          <select
            aria-label="Idioma de la vista previa del popup" value={lang} onChange={(e) => setLang(e.target.value)}
            className="h-9 rounded-md border border-default-200 bg-default-100 px-2 text-sm font-normal text-foreground outline-none focus:border-primary"
          >
            {ALL_LANGS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-default-200 p-3" data-translate-strip>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Idiomas</h3>
          <p className={`text-xs ${toTranslate.length ? 'text-warning' : 'text-default-500'}`}>
            {!ownTexts.length
              ? `Sin textos propios: el popup sale con los de fábrica, en los ${ALL_LANGS.length} idiomas de la app.`
              : toTranslate.length
                ? `${toTranslate.length} ${toTranslate.length === 1 ? 'texto sin traducir: en los demás idiomas se ve' : 'textos sin traducir: en los demás idiomas se ven'} en inglés.`
                : `Los ${ownTexts.length} textos propios están en los ${ALL_LANGS.length} idiomas de la app.`}
          </p>
        </div>
        {canWrite && ownTexts.length ? (
          <Button size="sm" variant={toTranslate.length ? 'flat' : 'light'} color="primary" onPress={() => setTranslating(true)} data-translate-open>
            {toTranslate.length ? 'Traducir con ChatGPT' : 'Volver a traducir'}
          </Button>
        ) : null}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,480px)]">
        {/* ── Formulario ── */}
        <div className="min-w-0 space-y-3">
          {P.FIELD_IDS.map((id) => (
            <TextRow
              key={id} label={LABELS[id][0]} hint={LABELS[id][1]} value={form[id]} limit={P.FIELDS[id].limit} factory={P.FIELDS[id]}
              long={id === 'desc'} stale={stale.has(id)} canWrite={canWrite} onChange={(value) => setField(id, value)}
            />
          ))}

          <div className="space-y-2 rounded-lg border border-default-200 p-3">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-sm font-semibold">Ventajas</span>
              <span className="text-xs text-default-500">
                {form.benefits.length ? `Salen las que escribas, en este orden (hasta ${P.MAX_BENEFITS}).` : 'Sin ninguna, salen las tres de fábrica.'}
              </span>
            </div>
            {form.benefits.map((benefit, index) => (
              <div key={index} className="rounded-md border border-default-200 p-2.5">
                <div className="flex items-start gap-2">
                  <span className="mt-2 w-4 shrink-0 text-center text-xs font-semibold text-default-400">{index + 1}</span>
                  <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
                    {LANGS.map((l) => (
                      <Input
                        key={l.id} size="sm" aria-label={`Ventaja ${index + 1} · ${l.label}`} placeholder={l.label} maxLength={P.BENEFIT_LIMIT} isReadOnly={!canWrite}
                        value={benefit[l.id] || ''} onValueChange={(v) => setBenefit(index, { ...benefit, [l.id]: v })}
                      />
                    ))}
                  </div>
                  {canWrite ? (
                    <div className="flex shrink-0 gap-1">
                      <Button size="sm" variant="light" isIconOnly aria-label="Subir" isDisabled={index === 0} onPress={() => moveBenefit(index, -1)}>↑</Button>
                      <Button size="sm" variant="light" isIconOnly aria-label="Bajar" isDisabled={index === form.benefits.length - 1} onPress={() => moveBenefit(index, 1)}>↓</Button>
                      <Button size="sm" variant="light" color="danger" isIconOnly aria-label="Quitar" onPress={() => reorder(form.benefits.filter((_, i) => i !== index))}>✕</Button>
                    </div>
                  ) : null}
                </div>
                <OtherLangs value={benefit} limit={P.BENEFIT_LIMIT} stale={stale.has(`benefit${index + 1}`)} canWrite={canWrite} onChange={(value) => setBenefit(index, value)} />
              </div>
            ))}
            {canWrite && form.benefits.length < P.MAX_BENEFITS ? (
              <Button size="sm" variant="flat" onPress={() => setForm((f) => ({ ...f, benefits: [...f.benefits, blank()] }))} data-add-benefit>
                Añadir ventaja
              </Button>
            ) : null}
          </div>

          {canWrite ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm" color="primary" isLoading={busy} isDisabled={!dirty} onPress={save} data-save-plan-modal>Guardar</Button>
              {!empty ? <Button size="sm" variant="light" onPress={() => { setForm(P.sanitize(null)); setStale(new Set()); }}>Volver a los de fábrica</Button> : null}
              {dirty && !message ? <span className="text-sm text-warning">Hay cambios sin guardar.</span> : null}
              {message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : null}
            </div>
          ) : (message ? <p role="status" className={`text-sm ${tone[message.type]}`}>{message.text}</p> : null)}
        </div>

        {/* ── Así se ve en la app ── */}
        <div className="min-w-0 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-default-500">Así se ve en la app</p>
          <div className="overflow-hidden rounded-xl border border-default-200 p-5" style={{ background: 'rgba(4, 6, 14, 0.9)' }}>
            <Preview form={form} lang={lang} />
          </div>
          <p className="text-xs text-default-500">
            «{SAMPLE_NAME.es}» es un nombre de muestra: ahí va lo que el usuario haya pulsado. La línea «Tu plan… no lo incluye» la pone la app
            según su plan. <strong className="font-semibold text-default-600">El precio y el código de descuento no se escriben aquí</strong>: salen
            solos del plan y del cupón vigente (el de Cupones), igual que en Gestionar suscripción, con el precio de antes tachado.
          </p>
        </div>
      </div>

      {translating ? (
        <TranslateDialog
          pending={toTranslate} everything={ownTexts} promptFor={translationPrompt} targets={ALL_LANGS.length - 1} example="title"
          onApply={applyTranslated} onClose={() => setTranslating(false)}
        />
      ) : null}
    </section>
  );
}
